// =====================================================================
// SecuriteRegistrePrint.jsx — M-010 : le registre sur papier
// =====================================================================
// Un inspecteur, un médecin du travail ou un assureur demande le registre
// « sur papier ». Cet écran produit exactement ce document : mise en page
// A4, une ligne par déclaration, les cinq mentions obligatoires de
// l'art. I.5-6 §3 nommées en clair pour qu'on puisse vérifier qu'aucune
// ne manque.
//
// Deux formats, parce que ce ne sont pas les mêmes demandes :
//   · le REGISTRE — la période entière, une ligne par déclaration ;
//     c'est ce qu'on présente à un contrôle
//   · la FICHE — une déclaration par page, tous ses champs et son
//     journal ; c'est ce qu'on joint à un dossier d'assurance
//
// Deux choses que le document dit de lui-même, et qui comptent :
//   · la date d'extraction et QUI l'a extrait. Un registre imprimé sans
//     cela ne se situe pas dans le temps, et le registre continue de vivre
//     après l'impression.
//   · qu'il contient des données de santé. Il ne se laisse pas traîner
//     sur une table de réunion.
//
// Impression par le navigateur (Ctrl+P → « Enregistrer au format PDF ») :
// même technique que les autocollants, aucune dépendance de plus.
// =====================================================================

import { useState, useMemo } from 'react';
import { LIBELLE_POINT } from '../lib/lieuPlan.js';

const C = {
  line: '#e5e7eb', ink: '#3f4654', mute: '#7b8494', brand: '#714B67',
  crit: '#dc2626',
};

// Les cinq mentions obligatoires, dans l'ordre du texte légal. Les colonnes
// du registre imprimé sont construites à partir de cette liste — ainsi le
// document et le texte legal ne peuvent pas diverger l'un de l'autre.
const MENTIONS = [
  { key: 'victim',    legal: '1°', head: 'Nom de la victime' },
  { key: 'when',      legal: '2°', head: 'Date, heure et lieu' },
  { key: 'what',      legal: '3°', head: 'Circonstances' },
  { key: 'injury',    legal: '4°', head: 'Nature et siège de la lésion' },
  { key: 'care',      legal: '5°', head: 'Secouriste et soins donnés' },
  { key: 'witnesses', legal: '—',  head: 'Témoins' },
];

// Repris tel quel des statuts du service (safety-incident.service.js).
// Un statut non traduit sortirait sur le document sous forme de constante
// technique : sur un registre montre a l'inspection, c'est exclu.
const STATUS_FR = {
  BROUILLON: 'brouillon — qualification en attente',
  SOS_EN_COURS: 'SOS en cours',
  REGISTRE_BENIN: 'accident bénin — inscrit au registre',
  // 23-10-2026 : accident non bénin, grave ou très grave pas encore déclaré
  A_DECLARER_ASSUREUR: 'à déclarer à l’assureur (8 jours calendrier)',
  DECLARE_ASSUREUR: 'déclaré à l’assureur',
  AGGRAVE_A_DECLARER: 'aggravé — à déclarer',
  CLOS: 'clos',
};

const dt = (d) => (d ? new Date(d).toLocaleString('fr-BE', {
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit',
}) : '—');
const dOnly = (d) => (d ? new Date(d).toLocaleDateString('fr-BE', {
  day: '2-digit', month: '2-digit', year: 'numeric',
}) : '—');

const btn = (primary) => ({
  padding: '.5rem 1rem', borderRadius: 8, fontWeight: 600, cursor: 'pointer',
  border: primary ? 'none' : `1px solid ${C.line}`,
  background: primary ? C.brand : '#fff', color: primary ? '#fff' : C.brand,
});

export default function SecuriteRegistrePrint({ rows = [], printedBy, onClose, focus = null }) {
  const [mode, setMode] = useState(focus ? 'fiche' : 'registre');

  // Le registre se lit dans l'ordre où les choses sont arrivées, pas dans
  // l'ordre inverse : c'est un registre, pas un fil d'actualité.
  const list = useMemo(() => {
    const l = focus ? rows.filter((r) => r.id === focus) : [...rows];
    return l.sort((a, b) => new Date(a.occurredAt) - new Date(b.occurredAt));
  }, [rows, focus]);

  const period = useMemo(() => {
    if (!list.length) return null;
    return { from: list[0].occurredAt, to: list[list.length - 1].occurredAt };
  }, [list]);

  const extractedAt = dt(new Date());

  return (
    <div>
      <style>{`
        @media print {
          @page { size: A4 ${mode === 'registre' ? 'landscape' : 'portrait'}; margin: 12mm 10mm; }
          body * { visibility: hidden !important; }
          #impression, #impression * { visibility: visible !important; }
          #impression { position: absolute; left: 0; top: 0; width: 100%; }
          .noprint { display: none !important; }
          .fiche { break-after: page; }
          .fiche:last-child { break-after: auto; }
          thead { display: table-header-group; }   /* en-tête répété par page */
          tr { break-inside: avoid; }
        }
      `}</style>

      <div className="noprint" style={{
        display: 'flex', gap: '.6rem', alignItems: 'center', flexWrap: 'wrap',
        padding: '.8rem 1rem', background: '#fff', border: `1px solid ${C.line}`,
        borderRadius: 12, marginBottom: '.9rem',
      }}>
        <b style={{ fontSize: '.92rem', color: C.ink }}>Impression du registre</b>
        <div style={{ display: 'flex', gap: 0, border: `1px solid ${C.line}`, borderRadius: 8 }}>
          {[['registre', 'Registre (une ligne par déclaration)'],
            ['fiche', 'Fiches (une page par déclaration)']].map(([k, l], i) => (
            <button key={k} type="button" onClick={() => setMode(k)} style={{
              border: 'none', background: mode === k ? '#eef2ff' : 'transparent',
              color: mode === k ? '#4338ca' : '#475569', fontWeight: 600,
              fontSize: '.84rem', padding: '.45rem .8rem', cursor: 'pointer',
              borderRadius: i === 0 ? '7px 0 0 7px' : '0 7px 7px 0',
            }}>{l}</button>
          ))}
        </div>
        <span style={{ fontSize: '.85rem', color: C.mute }}>
          {list.length} déclaration(s)
          {mode === 'registre' && ' · imprimé en paysage'}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '.5rem' }}>
          {onClose && (
            <button type="button" onClick={onClose} style={btn(false)}>Fermer</button>
          )}
          <button type="button" disabled={!list.length}
            onClick={() => window.print()} style={btn(true)}>
            Imprimer / PDF
          </button>
        </div>
      </div>

      {!list.length && (
        <p className="noprint" style={{ color: C.mute }}>
          Aucune déclaration sur la période : rien à imprimer.
        </p>
      )}

      {/* ---------------- le document ---------------- */}
      <div id="impression" style={{
        background: '#fff', color: '#111827', padding: '10mm',
        border: `1px solid ${C.line}`, borderRadius: 8,
        fontFamily: 'system-ui, sans-serif',
      }}>
        {mode === 'registre' ? (
          <>
            <Entete titre="Registre des premiers soins et des accidents du travail"
              period={period} count={list.length}
              printedBy={printedBy} extractedAt={extractedAt} />

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.6pt' }}>
              <thead>
                <tr>
                  <th style={th}>N°</th>
                  {MENTIONS.map((m) => (
                    <th key={m.key} style={th}>
                      <div>{m.head}</div>
                      <div style={{ fontWeight: 400, fontSize: '7pt', color: '#6b7280' }}>
                        {m.legal === '—' ? 'complément' : `art. I.5-6 §3, ${m.legal}`}
                      </div>
                    </th>
                  ))}
                  <th style={th}>Qualification et suites</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.ref}</td>
                    <td style={td}>
                      {r.victimName || '—'}
                      {r.victimStatus && r.victimStatus !== 'INTERNE' && (
                        <div style={{ fontSize: '7.4pt', color: '#6b7280' }}>
                          {r.victimStatus === 'INTERIMAIRE' ? 'intérimaire' : 'tiers'}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {dt(r.occurredAt)}
                      <div style={{ fontSize: '7.4pt', color: '#6b7280' }}>
                        {r.locationText || r.asset?.name || r.asset?.code || (r.planId ? LIBELLE_POINT : '—')}
                      </div>
                    </td>
                    <td style={td}>{r.circumstances || r.description || '—'}</td>
                    <td style={td}>
                      {r.injuryNature || '—'}
                      {r.injurySite && (
                        <div style={{ fontSize: '7.4pt', color: '#6b7280' }}>{r.injurySite}</div>
                      )}
                    </td>
                    <td style={td}>
                      {r.firstAiderName || '—'}
                      {r.careGiven && (
                        <div style={{ fontSize: '7.4pt', color: '#374151' }}>{r.careGiven}</div>
                      )}
                    </td>
                    <td style={td}>{r.witnesses || '—'}</td>
                    <td style={td}>
                      {STATUS_FR[r.status] || r.status}
                      {r.insurerRef && (
                        <div style={{ fontSize: '7.4pt' }}>assureur : {r.insurerRef}</div>
                      )}
                      {r.insurerDeadline && !r.insurerDeclaredAt && (
                        <div style={{ fontSize: '7.4pt', color: C.crit }}>
                          à déclarer avant le {dOnly(r.insurerDeadline)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pied />
          </>
        ) : (
          list.map((r) => (
            <div key={r.id} className="fiche" style={{ marginBottom: '14mm' }}>
              <Entete titre={`Déclaration ${r.ref}`} period={null} count={null}
                printedBy={printedBy} extractedAt={extractedAt} />

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9.5pt' }}>
                <tbody>
                  <Ligne legal="1°" k="Nom de la victime"
                    v={[r.victimName, r.victimStatus && r.victimStatus !== 'INTERNE'
                      ? (r.victimStatus === 'INTERIMAIRE' ? '(intérimaire)' : '(tiers)') : null]
                      .filter(Boolean).join(' ')} />
                  <Ligne legal="2°" k="Date et heure" v={dt(r.occurredAt)} />
                  <Ligne legal="2°" k="Lieu"
                    v={r.locationText || r.asset?.name || r.asset?.code || (r.planId ? LIBELLE_POINT : null)} />
                  <Ligne legal="3°" k="Circonstances" v={r.circumstances} />
                  <Ligne legal="3°" k="Description" v={r.description} />
                  <Ligne legal="4°" k="Nature de la lésion" v={r.injuryNature} />
                  <Ligne legal="4°" k="Siège de la lésion" v={r.injurySite} />
                  <Ligne legal="5°" k="Personne ayant donné les soins"
                    v={r.firstAiderName} />
                  <Ligne legal="5°" k="Soins donnés" v={r.careGiven} />
                  <Ligne k="Témoins" v={r.witnesses} />
                  <Ligne k="Suite donnée" v={r.followUp} />
                  <Ligne k="Qualification" v={STATUS_FR[r.status] || r.status} />
                  <Ligne k="Déclaré par" v={r.declaredByName} />
                  <Ligne k="Déclaré le" v={dt(r.createdAt)} />
                  {r.insurerRef && <Ligne k="Référence assureur" v={r.insurerRef} />}
                  {r.insurerDeclaredAt && (
                    <Ligne k="Déclaré à l’assureur le" v={dOnly(r.insurerDeclaredAt)} />
                  )}
                  {r.insurerDeadline && !r.insurerDeclaredAt && (
                    <Ligne k="À déclarer à l’assureur avant le"
                      v={dOnly(r.insurerDeadline)} alert />
                  )}
                  {r.graveReportDue && (
                    <Ligne k="Rapport circonstancié dû le"
                      v={dOnly(r.graveReportDue)} alert />
                  )}
                  {r.closedAt && (
                    <Ligne k="Clôturé le"
                      v={`${dt(r.closedAt)}${r.closedByName ? ` par ${r.closedByName}` : ''}`} />
                  )}
                </tbody>
              </table>

              <Pied />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const th = {
  textAlign: 'left', padding: '4px 5px', border: '0.6pt solid #9ca3af',
  background: '#f3f4f6', fontWeight: 700, verticalAlign: 'top',
};
const td = {
  padding: '4px 5px', border: '0.6pt solid #9ca3af', verticalAlign: 'top',
};

function Ligne({ legal, k, v, alert }) {
  return (
    <tr>
      <td style={{ ...td, width: '14mm', color: '#6b7280', fontSize: '8pt',
        whiteSpace: 'nowrap' }}>{legal || ''}</td>
      <td style={{ ...td, width: '62mm', fontWeight: 600, background: '#f9fafb' }}>{k}</td>
      <td style={{ ...td, color: alert ? C.crit : '#111827',
        fontWeight: alert ? 700 : 400 }}>{v || '—'}</td>
    </tr>
  );
}

function Entete({ titre, period, count, printedBy, extractedAt }) {
  return (
    <div style={{ borderBottom: '1.2pt solid #111827', paddingBottom: '3mm',
      marginBottom: '4mm' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', gap: '8mm' }}>
        <div>
          <div style={{ fontSize: '13pt', fontWeight: 700 }}>{titre}</div>
          <div style={{ fontSize: '9pt', color: '#374151', marginTop: '1mm' }}>
            Brain E-Log — Code du bien-être au travail, Livre I, Titre 5
            (art. I.5-6 §3)
          </div>
          {period && (
            <div style={{ fontSize: '9pt', marginTop: '1mm' }}>
              Période : du <b>{dOnly(period.from)}</b> au <b>{dOnly(period.to)}</b>
              {count != null && <> — <b>{count}</b> déclaration(s)</>}
            </div>
          )}
        </div>
        <div style={{ fontSize: '8pt', color: '#374151', textAlign: 'right',
          whiteSpace: 'nowrap' }}>
          <div>Extrait le {extractedAt}</div>
          {printedBy && <div>par {printedBy}</div>}
        </div>
      </div>
    </div>
  );
}

function Pied() {
  return (
    <p style={{ fontSize: '7.6pt', color: '#4b5563', marginTop: '4mm',
      borderTop: '0.6pt solid #9ca3af', paddingTop: '2mm' }}>
      <b>Document contenant des données relatives à la santé.</b> Accès limité
      aux personnes habilitées (conseiller en prévention, médecin du travail,
      inspection). Le registre reste tenu dans l’application : cet extrait
      reflète son état à la date indiquée ci-dessus.
    </p>
  );
}
